import { useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { revisionsInOrder, type Project, localeTag } from '@elec/domain';
import type { Translate } from '@elec/i18n';
import {
  CloudError,
  isNotConfirmed,
  type LibraryItem,
  type Member,
  type ProjectSummary,
  type Role,
  type Share,
  type Team,
} from '@elec/sync';
import { legalUrl, type CloudManager, type CloudState } from './cloud.ts';
import { Dialog } from './ui.tsx';

const ROLES: Role[] = ['owner', 'editor', 'viewer'];
type UiLocale = 'fr' | 'en' | 'nl';

/** What the account dialog opens on (the account menu in the top bar picks it). */
export type AccountView = 'projects' | 'members' | 'templates' | 'delete' | 'newTeam';

/* ---------- small pieces ---------- */

const PATHS = {
  close: 'M6 6l12 12M18 6L6 18',
  check: 'M5 12l5 5 9-10',
  shield: 'M12 3l8 3v6c0 4.5-3.4 8-8 9-4.6-1-8-4.5-8-9V6z',
  eye: 'M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7S2 12 2 12zM12 9a3 3 0 1 0 0 6 3 3 0 0 0 0-6z',
  eyeOff:
    'M3 3l18 18M10.6 5.1A10 10 0 0 1 12 5c6.4 0 10 7 10 7a17 17 0 0 1-3.2 4M6.6 6.6C3.8 8.4 2 12 2 12s3.6 7 10 7c1.7 0 3.2-.5 4.5-1.2M9.9 9.9a3 3 0 0 0 4.2 4.2',
  user: 'M12 4a4 4 0 1 0 0 8 4 4 0 0 0 0-8zM4 21c0-4 3.6-7 8-7s8 3 8 7',
  team: 'M9 4.5a3.5 3.5 0 1 0 0 7 3.5 3.5 0 0 0 0-7zM2.5 20c0-3.6 2.9-6 6.5-6s6.5 2.4 6.5 6M16 4.5a3.5 3.5 0 0 1 0 7M18 14.3c2.1.7 3.5 2.8 3.5 5.7',
  plus: 'M12 5v14M5 12h14',
  logout: 'M15 4h3a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2h-3M10 17l-5-5 5-5M5 12h11',
  trash: 'M4 7h16M10 11v6M14 11v6M6 7l1 13h10l1-13M9 7V4h6v3',
  search: 'M11 4a7 7 0 1 0 0 14 7 7 0 0 0 0-14zM20 20l-4-4',
  upload: 'M12 16V4M7 9l5-5 5 5M5 20h14',
  folder: 'M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z',
  doc: 'M6 3h12a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2zM8 8h8M8 12h8M8 16h5',
  mail: 'M5 5h14a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2zM3.5 6.5L12 13l8.5-6.5',
  alert: 'M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18zM12 7.5V13M12 16.5v.01',
} as const;

function Glyph({ name, size = 16 }: { name: keyof typeof PATHS; size?: number }) {
  return (
    <svg
      className="glyph"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d={PATHS[name]} />
    </svg>
  );
}

/** The socket icon (docs/brand). Its colours come from --logo-* so it suits the ground it sits on. */
function TabloIcon({ size, className }: { size: number; className?: string }) {
  return (
    <svg
      className={`tablo-icon ${className ?? ''}`}
      width={size}
      height={size}
      viewBox="0 0 64 64"
      aria-hidden="true"
    >
      <rect width="64" height="64" rx="15" style={{ fill: 'var(--logo-tile)' }} />
      <circle cx="32" cy="34" r="20" style={{ fill: 'var(--logo-face)' }} />
      <circle cx="24" cy="35.5" r="4.2" style={{ fill: 'var(--logo-tile)' }} />
      <circle cx="40" cy="35.5" r="4.2" style={{ fill: 'var(--logo-tile)' }} />
      <rect x="29.5" y="16.5" width="5" height="8.5" rx="1.5" style={{ fill: 'var(--logo-pin)' }} />
    </svg>
  );
}

const initialOf = (s: string) => s.trim().charAt(0).toUpperCase() || '?';

function Avatar({ name, size = 32 }: { name: string; size?: number }) {
  return (
    <span
      className="acct-avatar"
      style={{ width: size, height: size, fontSize: Math.round(size * 0.4) }}
      aria-hidden="true"
    >
      {initialOf(name)}
    </span>
  );
}

function CloseButton({ t, onClose }: { t: Translate; onClose(): void }) {
  return (
    <button
      type="button"
      className="dialog-x"
      aria-label={t('dialog.close')}
      onClick={onClose}
      data-testid="cloud-close"
    >
      <Glyph name="close" />
    </button>
  );
}

function ErrorNote({ children }: { children: ReactNode }) {
  return (
    <p className="auth-error warn" role="alert">
      <Glyph name="alert" size={15} />
      <span>{children}</span>
    </p>
  );
}

/* ---------- signed out: sign in / create an account ---------- */

/**
 * Signed-out frame: the graphite brand panel on the left, the form on the right. On narrow
 * screens the panel goes and the form becomes a centred card with the icon on top (styles.css).
 */
function AuthShell({ t, onClose, children }: { t: Translate; onClose(): void; children: ReactNode }) {
  return (
    <div className="auth">
      <aside className="auth-brand">
        <svg className="auth-brand-socket" viewBox="0 0 64 64" aria-hidden="true">
          <circle cx="32" cy="34" r="20" />
          <circle cx="24" cy="35.5" r="3.8" />
          <circle cx="40" cy="35.5" r="3.8" />
        </svg>
        <div className="auth-brand-name">
          <TabloIcon size={36} />
          <span>{t('cloud.title')}</span>
        </div>
        <h2>{t('cloud.brandTitle')}</h2>
        <ul>
          {(['cloud.brand1', 'cloud.brand2', 'cloud.brand3'] as const).map((k) => (
            <li key={k}>
              <Glyph name="check" />
              <span>{t(k)}</span>
            </li>
          ))}
        </ul>
        <span className="grow" />
        <p className="auth-europe">
          <Glyph name="shield" size={14} />
          {t('cloud.dataEurope')}
        </p>
      </aside>
      <div className="auth-main">
        <CloseButton t={t} onClose={onClose} />
        {children}
      </div>
    </div>
  );
}

/** Screen title. `compactOnly`: the wide layout says it with its tabs, so only the card shows it. */
function AuthHead({ title, sub, compactOnly }: { title: string; sub?: string; compactOnly?: boolean }) {
  return (
    <header className={`auth-head${compactOnly ? ' compact-only' : ''}`}>
      <TabloIcon size={48} className="auth-head-icon" />
      <h2>{title}</h2>
      {sub && <p>{sub}</p>}
    </header>
  );
}

function Field(props: {
  id: string;
  label: string;
  aside?: ReactNode;
  children: ReactNode;
  hint?: ReactNode;
}) {
  return (
    <div className="auth-field">
      <div className="auth-label">
        <label htmlFor={props.id}>{props.label}</label>
        {props.aside}
      </div>
      {props.children}
      {props.hint}
    </div>
  );
}

function PasswordInput(props: {
  id: string;
  value: string;
  onChange(v: string): void;
  autoComplete: string;
  testId: string;
  t: Translate;
  autoFocus?: boolean;
}) {
  const [shown, setShown] = useState(false);
  return (
    <div className="auth-pass">
      <input
        id={props.id}
        type={shown ? 'text' : 'password'}
        value={props.value}
        onChange={(e) => props.onChange(e.target.value)}
        autoComplete={props.autoComplete}
        autoFocus={props.autoFocus}
        data-testid={props.testId}
      />
      <button
        type="button"
        className="auth-eye"
        aria-label={props.t(shown ? 'cloud.hidePassword' : 'cloud.showPassword')}
        aria-pressed={shown}
        onClick={() => setShown((s) => !s)}
      >
        <Glyph name={shown ? 'eyeOff' : 'eye'} />
      </button>
    </div>
  );
}

function PasswordHint({ t, password }: { t: Translate; password: string }) {
  const ok = password.length >= 8;
  return (
    <span className={`auth-hint${ok ? ' ok' : ''}`}>
      {ok && <Glyph name="check" size={14} />}
      {t('cloud.passwordHint')}
    </span>
  );
}

function LegalNote({ cloud, t, locale }: { cloud: CloudManager; t: Translate; locale: UiLocale }) {
  return (
    <p className="auth-legal">
      {t('legal.registerNote')}{' '}
      <button
        type="button"
        className="link"
        onClick={() => void cloud.openUrl(legalUrl('privacy', locale))}
        data-testid="cloud-privacy"
      >
        {t('legal.privacy')}
      </button>
      .
    </p>
  );
}

/** Sign in / create an account. */
function SignIn({
  cloud,
  t,
  locale,
  mail,
}: {
  cloud: CloudManager;
  t: Translate;
  locale: UiLocale;
  mail: boolean;
}) {
  const [mode, setMode] = useState<'login' | 'register' | 'forgot' | 'pending'>('login');
  const [sent, setSent] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const go = (m: typeof mode) => {
    setMode(m);
    setError(null);
  };
  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      if (mode === 'login') await cloud.login(email, password);
      else if (mode === 'register') {
        if ((await cloud.register(email, password, name, locale)) === 'pending') setMode('pending');
      } else {
        await cloud.forgotPassword(email, locale);
        setSent(true);
      }
    } catch (e) {
      // Signed up earlier, link not opened yet: back to "check your inbox".
      if (isNotConfirmed(e)) setMode('pending');
      else setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  const onSubmit = (e: { preventDefault(): void }) => {
    e.preventDefault();
    void submit();
  };
  if (mode === 'pending')
    return (
      <PendingConfirmation
        cloud={cloud}
        t={t}
        locale={locale}
        email={email}
        onSignIn={() => {
          go('login');
          setPassword('');
        }}
      />
    );
  const emailInput = (
    <input
      id="c-email"
      type="email"
      value={email}
      onChange={(e) => setEmail(e.target.value)}
      autoComplete="email"
      placeholder={t('cloud.emailPlaceholder')}
      data-testid="cloud-email"
    />
  );
  if (mode === 'forgot')
    return (
      <form className="auth-form" onSubmit={onSubmit}>
        <AuthHead title={t('cloud.forgotTitle')} sub={sent ? undefined : t('cloud.forgotIntro')} />
        {sent ? (
          <p className="auth-sent" data-testid="cloud-forgot-sent">
            <Glyph name="mail" />
            <span>{t('cloud.forgotSent', { email })}</span>
          </p>
        ) : (
          <>
            <Field id="c-email" label={t('cloud.email')}>
              {emailInput}
            </Field>
            {error && <ErrorNote>{error}</ErrorNote>}
            <button
              className="primary auth-submit"
              type="submit"
              disabled={busy || !email}
              data-testid="cloud-forgot-send"
            >
              {t('cloud.forgotSend')}
            </button>
          </>
        )}
        <button
          type="button"
          className="link auth-back"
          onClick={() => {
            go('login');
            setSent(false);
          }}
        >
          {t('cloud.backToLogin')}
        </button>
      </form>
    );
  const login = mode === 'login';
  const intro = login ? t('cloud.loginIntro') : t('cloud.intro');
  return (
    <form className="auth-form" onSubmit={onSubmit}>
      <AuthHead compactOnly title={t(login ? 'cloud.loginTitle' : 'cloud.registerTitle')} sub={intro} />
      <div className="auth-tabs" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={login}
          onClick={() => go('login')}
          data-testid="cloud-tab-login"
        >
          {t('cloud.login')}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={!login}
          onClick={() => go('register')}
          data-testid="cloud-tab-register"
        >
          {t('cloud.register')}
        </button>
      </div>
      <p className="auth-intro">{intro}</p>
      <div className={login ? 'auth-col' : 'auth-row'}>
        {!login && (
          <Field id="c-name" label={t('cloud.name')}>
            <input
              id="c-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoComplete="name"
              data-testid="cloud-name"
            />
          </Field>
        )}
        <Field id="c-email" label={t('cloud.email')}>
          {emailInput}
        </Field>
      </div>
      <Field
        id="c-pass"
        label={t('cloud.password')}
        aside={
          login &&
          mail && (
            <button type="button" className="link" onClick={() => go('forgot')} data-testid="cloud-forgot">
              {t('cloud.forgot')}
            </button>
          )
        }
        hint={!login && <PasswordHint t={t} password={password} />}
      >
        <PasswordInput
          id="c-pass"
          value={password}
          onChange={setPassword}
          autoComplete={login ? 'current-password' : 'new-password'}
          testId="cloud-password"
          t={t}
        />
      </Field>
      {!login && mail && (
        <p className="auth-info">
          <Glyph name="mail" />
          <span>{t('cloud.registerConfirmNote')}</span>
        </p>
      )}
      {error && <ErrorNote>{error}</ErrorNote>}
      <button
        className="primary auth-submit"
        type="submit"
        disabled={busy || !email || !password}
        data-testid="cloud-submit"
      >
        {login ? t('cloud.login') : t('cloud.register')}
      </button>
      <div className="auth-switch">
        <span className="auth-or">{t(login ? 'cloud.noAccount' : 'cloud.haveAccount')}</span>
        <button
          type="button"
          className="auth-alt"
          onClick={() => go(login ? 'register' : 'login')}
          data-testid="cloud-switch"
        >
          {t(login ? 'cloud.register' : 'cloud.login')}
        </button>
      </div>
      <LegalNote cloud={cloud} t={t} locale={locale} />
    </form>
  );
}

/** After sign-up: the account works once the emailed link is opened. */
function PendingConfirmation(props: {
  cloud: CloudManager;
  t: Translate;
  locale: UiLocale;
  email: string;
  onSignIn(): void;
}) {
  const { t } = props;
  const [resent, setResent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const resend = async () => {
    setBusy(true);
    setError(null);
    try {
      await props.cloud.resendConfirmation(props.email, props.locale);
      setResent(true);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="auth-form" data-testid="cloud-pending">
      <AuthHead title={t('cloud.pendingTitle')} />
      <p className="auth-sent">
        <Glyph name="mail" />
        <span>{t('cloud.pendingBody', { email: props.email })}</span>
      </p>
      <p className="auth-intro">{t('cloud.pendingSpam')}</p>
      {error && <ErrorNote>{error}</ErrorNote>}
      {resent ? (
        <p className="auth-hint ok" role="status" data-testid="cloud-resent">
          <Glyph name="check" size={14} />
          {t('cloud.resent')}
        </p>
      ) : (
        <button
          type="button"
          className="auth-alt"
          onClick={() => void resend()}
          disabled={busy}
          data-testid="cloud-resend"
        >
          {t('cloud.resend')}
        </button>
      )}
      <button
        type="button"
        className="link auth-back"
        onClick={props.onSignIn}
        data-testid="cloud-pending-signin"
      >
        {t('cloud.pendingSignIn')}
      </button>
    </div>
  );
}

/** The emailed confirmation link (#/confirm/<token>): confirms, then signs in here. */
function ConfirmEmail(props: {
  cloud: CloudManager;
  t: Translate;
  token: string;
  onDone(confirmed: boolean): void;
}) {
  const { t } = props;
  const [state, setState] = useState<'busy' | 'expired' | { error: string }>('busy');
  const started = useRef(false);
  const confirm = async () => {
    setState('busy');
    try {
      await props.cloud.confirmEmail(props.token);
      props.onDone(true);
    } catch (e) {
      if ((e as { status?: number }).status === 410) setState('expired');
      else setState({ error: (e as Error).message });
    }
  };
  useEffect(() => {
    // The link works once: never send it twice (React runs effects twice in development).
    if (started.current) return;
    started.current = true;
    void confirm();
  }, []);
  return (
    <div className="auth-form" data-testid="cloud-confirm">
      <AuthHead title={t('cloud.confirmTitle')} />
      {state === 'busy' ? (
        <p className="auth-intro">{t('cloud.confirming')}</p>
      ) : state === 'expired' ? (
        <>
          <ErrorNote>
            <span data-testid="cloud-confirm-expired">{t('cloud.confirmExpired')}</span>
          </ErrorNote>
          <button type="button" className="auth-alt" onClick={() => props.onDone(false)}>
            {t('cloud.backToLogin')}
          </button>
        </>
      ) : (
        <>
          <ErrorNote>{state.error}</ErrorNote>
          <button type="button" className="auth-alt" onClick={() => void confirm()}>
            {t('cloud.confirmRetry')}
          </button>
        </>
      )}
    </div>
  );
}

/** New password from the emailed reset link (#/reset/<token>). */
function ResetPassword(props: {
  cloud: CloudManager;
  t: Translate;
  locale: UiLocale;
  token: string;
  onDone(): void;
}) {
  const { t } = props;
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [expired, setExpired] = useState(false);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await props.cloud.resetPassword(props.token, password, props.locale);
      props.onDone();
    } catch (e) {
      const status = (e as { status?: number }).status;
      if (status === 410) setExpired(true);
      else setError(status === 400 ? t('cloud.passwordTooShort') : (e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  if (expired)
    return (
      <div className="auth-form">
        <AuthHead title={t('cloud.resetTitle')} />
        <ErrorNote>
          <span data-testid="cloud-reset-expired">{t('cloud.resetExpired')}</span>
        </ErrorNote>
        <button type="button" className="auth-alt" onClick={props.onDone}>
          {t('cloud.backToLogin')}
        </button>
      </div>
    );
  return (
    <form
      className="auth-form"
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
    >
      <AuthHead title={t('cloud.resetTitle')} sub={t('cloud.resetIntro')} />
      <Field id="c-newpass" label={t('cloud.newPassword')} hint={<PasswordHint t={t} password={password} />}>
        <PasswordInput
          id="c-newpass"
          value={password}
          onChange={setPassword}
          autoComplete="new-password"
          autoFocus
          testId="cloud-new-password"
          t={t}
        />
      </Field>
      {error && <ErrorNote>{error}</ErrorNote>}
      <button
        className="primary auth-submit"
        type="submit"
        disabled={busy || !password}
        data-testid="cloud-reset-save"
      >
        {t('cloud.resetSave')}
      </button>
    </form>
  );
}

/* ---------- signed in: the account ---------- */

/**
 * Delete the account, from the app (the App Store and Google Play require it): what goes and
 * what stays, then the password to confirm.
 */
function DeleteAccount(props: {
  cloud: CloudManager;
  t: Translate;
  locale: UiLocale;
  email: string;
  offline: boolean;
  onCancel(): void;
  onDone(): void;
}) {
  const { t } = props;
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await props.cloud.deleteAccount(password, props.locale);
      props.onDone();
    } catch (e) {
      setError(
        e instanceof CloudError && e.status === 403 ? t('cloud.deleteWrongPassword') : (e as Error).message,
      );
      setBusy(false);
    }
  };
  return (
    <form
      className="acct-delete"
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
      data-testid="cloud-delete-form"
    >
      <p>{t('cloud.deleteIntro', { email: props.email })}</p>
      <ul className="cloud-delete-list">
        <li>{t('cloud.deleteWhat')}</li>
        <li>{t('cloud.deleteShared')}</li>
        <li>{t('cloud.deleteLocal')}</li>
      </ul>
      {props.offline ? (
        <p className="warn" data-testid="cloud-delete-offline">
          {t('cloud.deleteOffline')}
        </p>
      ) : (
        <Field id="c-delpass" label={t('cloud.deletePassword')}>
          <PasswordInput
            id="c-delpass"
            value={password}
            onChange={setPassword}
            autoComplete="current-password"
            autoFocus
            testId="cloud-delete-password"
            t={t}
          />
        </Field>
      )}
      {error && <p className="warn">{error}</p>}
      <div className="acct-delete-actions">
        <button type="button" className="auth-alt" onClick={props.onCancel} data-testid="cloud-delete-cancel">
          {t('dialog.cancel')}
        </button>
        <button
          className="primary danger"
          type="submit"
          disabled={busy || props.offline || !password}
          data-testid="cloud-delete-confirm"
        >
          {t('cloud.deleteConfirm')}
        </button>
      </div>
    </form>
  );
}

/** Workspace members (owners manage them). */
function Members({
  cloud,
  t,
  teamId,
  role,
  meId,
  onCount,
}: {
  cloud: CloudManager;
  t: Translate;
  teamId: string;
  role: Role;
  meId: string;
  onCount(n: number): void;
}) {
  const [members, setMembers] = useState<Member[]>([]);
  const [email, setEmail] = useState('');
  const [newRole, setNewRole] = useState<Role>('editor');
  const [error, setError] = useState<string | null>(null);
  const load = () =>
    void cloud
      .client!.members(teamId)
      .then((m) => {
        setMembers(m);
        onCount(m.length);
      })
      .catch((e: Error) => setError(e.message));
  useEffect(load, [teamId]);
  const act = async (fn: () => Promise<unknown>) => {
    setError(null);
    try {
      await fn();
      load();
    } catch (e) {
      setError((e as Error).message);
    }
  };
  const owner = role === 'owner';
  return (
    <div className="acct-members">
      <table className="members">
        <tbody>
          {members.map((m) => (
            <tr key={m.id}>
              <td className="acct-who">
                <Avatar name={m.name || m.email} />
                <span>
                  <strong>
                    {m.name}
                    {m.id === meId && <span className="muted"> ({t('cloud.you')})</span>}
                  </strong>
                  <span className="acct-mono">{m.email}</span>
                </span>
              </td>
              <td className="acct-members-role">
                {owner && m.id !== meId ? (
                  <select
                    value={m.role}
                    aria-label={`${t('cloud.roleOf')} ${m.name || m.email}`}
                    onChange={(e) =>
                      void act(() => cloud.client!.setRole(teamId, m.id, e.target.value as Role))
                    }
                  >
                    {ROLES.map((r) => (
                      <option key={r} value={r}>
                        {t(`cloud.role.${r}`)}
                      </option>
                    ))}
                  </select>
                ) : (
                  t(`cloud.role.${m.role}`)
                )}
              </td>
              <td className="acct-members-act">
                {owner && m.id !== meId && (
                  <button
                    className="acct-ghost"
                    onClick={() => void act(() => cloud.client!.removeMember(teamId, m.id))}
                  >
                    {t('cloud.remove')}
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {owner && (
        <form
          className="acct-invite"
          onSubmit={(e) => {
            e.preventDefault();
            if (email)
              void act(async () => (await cloud.client!.addMember(teamId, email, newRole), setEmail('')));
          }}
        >
          <input
            type="email"
            placeholder={t('cloud.memberEmail')}
            aria-label={t('cloud.memberEmail')}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            data-testid="member-email"
          />
          <select
            value={newRole}
            onChange={(e) => setNewRole(e.target.value as Role)}
            aria-label={t('cloud.roleOf')}
          >
            {ROLES.map((r) => (
              <option key={r} value={r}>
                {t(`cloud.role.${r}`)}
              </option>
            ))}
          </select>
          <button className="primary" type="submit" disabled={!email} data-testid="member-add">
            {t('cloud.addMember')}
          </button>
        </form>
      )}
      {error && <p className="warn">{error}</p>}
      <p className="acct-hint">{t('cloud.rolesHint')}</p>
    </div>
  );
}

const roleLabel = (t: Translate, r: Role) => t(`cloud.role.${r}`);

/** Signed in: workspaces on the left, the chosen workspace's projects, members and templates. */
function Account(props: {
  cloud: CloudManager;
  state: CloudState;
  t: Translate;
  locale: UiLocale;
  project: Project;
  start?: AccountView;
  onClose(): void;
  onOpen(id: string): void;
  onUpload(): Promise<void>;
  onNewFromTemplate(p: Project): void;
  onDeleted(): void;
}) {
  const { cloud, state, t } = props;
  const me = state.me!;
  const team = state.team;
  const s = props.start;
  const [tab, setTab] = useState<'projects' | 'members' | 'templates'>(
    s === 'members' || s === 'templates' ? s : 'projects',
  );
  const [deleting, setDeleting] = useState(s === 'delete');
  const [addingTeam, setAddingTeam] = useState(s === 'newTeam');
  const [projects, setProjects] = useState<ProjectSummary[] | null>(null);
  const [templates, setTemplates] = useState<LibraryItem[]>([]);
  const [memberCount, setMemberCount] = useState<number | null>(null);
  const [query, setQuery] = useState('');
  const [newTeam, setNewTeam] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (!team) return;
    setProjects(null);
    setMemberCount(null);
    setError(null);
    void cloud
      .client!.projects(team.id)
      .then(setProjects)
      .catch((e: Error) => setError(e.message));
    void cloud
      .client!.library(team.id)
      .then((items) => setTemplates(items.filter((i) => i.kind === 'template')))
      .catch(() => undefined);
    if (!team.personal)
      void cloud
        .client!.members(team.id)
        .then((m) => setMemberCount(m.length))
        .catch(() => undefined);
  }, [team?.id, cloud]);
  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  const date = (d: string) =>
    new Date(d).toLocaleString(localeTag(props.locale), { dateStyle: 'short', timeStyle: 'short' });
  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (projects ?? []).filter((p) => !q || (p.title || '').toLowerCase().includes(q));
  }, [projects, query]);
  const pickTeam = (tm: Team) => {
    cloud.selectTeam(tm);
    setDeleting(false);
  };
  const counts: Record<'projects' | 'members' | 'templates', number | undefined> = {
    projects: projects?.length,
    members: team?.personal ? undefined : (memberCount ?? undefined),
    templates: templates.length,
  };
  return (
    <div className="acct">
      <aside className="acct-rail">
        <div className="acct-me" data-testid="cloud-user">
          <Avatar name={me.user.name || me.user.email} size={40} />
          <span>
            <strong>{me.user.name}</strong>
            <span className="acct-mono">{me.user.email}</span>
          </span>
        </div>
        <span className="acct-label">{t('cloud.workspaces')}</span>
        <nav className="acct-teams" aria-label={t('cloud.workspaces')}>
          {me.teams.map((tm) => (
            <button
              key={tm.id}
              className="acct-item"
              aria-current={tm.id === team?.id && !deleting ? 'true' : undefined}
              onClick={() => pickTeam(tm)}
              data-cloud-team={tm.name}
            >
              <Glyph name={tm.personal ? 'user' : 'team'} />
              <span className="grow" title={tm.name}>
                {tm.name}
              </span>
            </button>
          ))}
          {addingTeam ? (
            <form
              className="acct-newteam"
              onSubmit={(e) => {
                e.preventDefault();
                if (!newTeam.trim()) return;
                void run(async () => {
                  const tm = await cloud.client!.createTeam(newTeam.trim());
                  setNewTeam('');
                  setAddingTeam(false);
                  await cloud.refreshMe();
                  pickTeam(tm);
                });
              }}
            >
              <input
                autoFocus
                placeholder={t('cloud.newWorkspace')}
                aria-label={t('cloud.newWorkspace')}
                value={newTeam}
                onChange={(e) => setNewTeam(e.target.value)}
                data-testid="team-name"
              />
              <button
                className="primary"
                type="submit"
                disabled={!newTeam.trim() || busy}
                data-testid="team-create"
              >
                {t('cloud.create')}
              </button>
            </form>
          ) : (
            <button className="acct-item acct-add" onClick={() => setAddingTeam(true)} data-testid="team-new">
              <Glyph name="plus" />
              <span>{t('cloud.newWorkspace')}</span>
            </button>
          )}
        </nav>
        <span className="grow" />
        <div className="acct-foot">
          <button className="acct-item" onClick={() => void cloud.logout()} data-testid="cloud-logout">
            <Glyph name="logout" />
            <span>{t('cloud.logout')}</span>
          </button>
          <button
            className="acct-item danger"
            aria-current={deleting ? 'true' : undefined}
            onClick={() => setDeleting(true)}
            data-testid="cloud-delete-account"
          >
            <Glyph name="trash" />
            <span>{t('cloud.deleteAccount')}</span>
          </button>
        </div>
      </aside>
      <section className="acct-main">
        <header className="acct-head">
          <h2>{deleting ? t('cloud.deleteTitle') : team?.name}</h2>
          {!deleting && team && <span className="acct-chip">{roleLabel(t, team.role)}</span>}
          <span className="grow" />
          <CloseButton t={t} onClose={props.onClose} />
        </header>
        {deleting ? (
          <DeleteAccount
            cloud={cloud}
            t={t}
            locale={props.locale}
            email={me.user.email}
            offline={state.offline}
            onCancel={() => setDeleting(false)}
            onDone={props.onDeleted}
          />
        ) : (
          team && (
            <>
              <div className="acct-tabs" role="tablist">
                {(['projects', 'members', 'templates'] as const).map((k) => (
                  <button
                    key={k}
                    role="tab"
                    aria-selected={tab === k}
                    onClick={() => setTab(k)}
                    data-testid={`cloud-tab-${k}`}
                  >
                    {t(`cloud.tab.${k}`)}
                    {counts[k] !== undefined && <span className="acct-count">{counts[k]}</span>}
                  </button>
                ))}
              </div>
              {tab === 'projects' && (
                <>
                  <div className="acct-tools">
                    <label className="acct-search">
                      <Glyph name="search" size={15} />
                      <span className="sr-only">{t('cloud.searchProjects')}</span>
                      <input
                        type="search"
                        placeholder={t('cloud.searchProjects')}
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        data-testid="cloud-search"
                      />
                    </label>
                    {team.role !== 'viewer' && (
                      <button
                        className="primary acct-upload"
                        disabled={busy || state.live?.projectId === props.project.id}
                        onClick={() => void run(props.onUpload)}
                        title={t('cloud.upload', { name: team.name })}
                        data-testid="cloud-upload"
                      >
                        <Glyph name="upload" size={15} />
                        {t('cloud.uploadHere')}
                      </button>
                    )}
                  </div>
                  {projects === null ? (
                    <p className="acct-empty">{t('ai.thinking')}</p>
                  ) : shown.length === 0 ? (
                    <p className="acct-empty">
                      {query.trim() ? t('cloud.noMatch', { q: query.trim() }) : t('cloud.noProjects')}
                    </p>
                  ) : (
                    <div className="acct-list">
                      <div className="acct-row acct-row-head" aria-hidden="true">
                        <span>{t('cloud.col.project')}</span>
                        <span>{t('cloud.col.updated')}</span>
                        <span>{t('cloud.col.by')}</span>
                      </div>
                      <ul data-testid="cloud-projects">
                        {shown.map((p) => (
                          <li key={p.id}>
                            <button
                              className="acct-row"
                              onClick={() => props.onOpen(p.id)}
                              data-cloud-project={p.title || p.id}
                            >
                              <span className="acct-title">
                                <Glyph name="doc" size={15} />
                                <strong>{p.title || t('cloud.untitled')}</strong>
                                {state.live?.projectId === p.id && (
                                  <span className="acct-tag">{t('cloud.openNow')}</span>
                                )}
                              </span>
                              <span className="acct-mono">{date(p.updated)}</span>
                              <span className="acct-by">{p.createdBy}</span>
                            </button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </>
              )}
              {tab === 'members' &&
                (team.personal ? (
                  <p className="acct-empty">{t('cloud.personal')}</p>
                ) : (
                  <Members
                    cloud={cloud}
                    t={t}
                    teamId={team.id}
                    role={team.role}
                    meId={me.user.id}
                    onCount={setMemberCount}
                  />
                ))}
              {tab === 'templates' && (
                <>
                  {team.role !== 'viewer' && (
                    <div className="acct-tools">
                      <button
                        className="auth-alt"
                        disabled={busy}
                        onClick={() =>
                          void run(async () => {
                            await cloud.client!.putLibrary(
                              team.id,
                              'template',
                              props.project.id,
                              props.project.meta.title || t('cloud.untitled'),
                              { project: stripForTemplate(props.project) },
                            );
                            setTemplates(
                              (await cloud.client!.library(team.id)).filter((i) => i.kind === 'template'),
                            );
                          })
                        }
                        data-testid="template-save"
                      >
                        <Glyph name="plus" size={15} />
                        {t('cloud.saveTemplate')}
                      </button>
                    </div>
                  )}
                  {templates.length === 0 ? (
                    <p className="acct-empty">{t('cloud.noTemplates')}</p>
                  ) : (
                    <div className="acct-list">
                      <div className="acct-row acct-row-head two" aria-hidden="true">
                        <span>{t('cloud.col.template')}</span>
                        <span>{t('cloud.col.updated')}</span>
                      </div>
                      <ul>
                        {templates.map((x) => (
                          <li key={x.id}>
                            <button
                              className="acct-row two"
                              onClick={() =>
                                props.onNewFromTemplate((x.data as { project: Project }).project)
                              }
                            >
                              <span className="acct-title">
                                <Glyph name="doc" size={15} />
                                <strong>{x.name}</strong>
                              </span>
                              <span className="acct-mono">{date(x.updated)}</span>
                            </button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </>
              )}
            </>
          )
        )}
        {error && <p className="warn acct-error">{error}</p>}
      </section>
    </div>
  );
}

/** Account, workspaces, cloud projects and team templates; signed out, sign in. */
export function CloudDialog(props: {
  cloud: CloudManager;
  state: CloudState;
  t: Translate;
  locale: UiLocale;
  project: Project;
  /** Where the account opens (from the account menu). */
  start?: AccountView;
  onClose(): void;
  onOpen(id: string): void;
  onUpload(): Promise<void>;
  onNewFromTemplate(p: Project): void;
  /** Token from an emailed reset link: ask for the new password first. */
  resetToken?: string | null;
  onResetDone?(): void;
  /** Token from an emailed confirmation link: confirm the address and sign in. */
  confirmToken?: string | null;
  onConfirmDone?(confirmed: boolean): void;
}) {
  const { cloud, state, t } = props;
  /** Signed out because the account was just deleted: say so. */
  const [deleted, setDeleted] = useState(false);
  const signedIn = !!state.me && !props.confirmToken && !props.resetToken;
  return (
    <Dialog
      title={t('cloud.title')}
      bare
      className={signedIn ? 'acct-dialog' : 'auth-dialog'}
      onClose={props.onClose}
    >
      {signedIn ? (
        <Account
          cloud={cloud}
          state={state}
          t={t}
          locale={props.locale}
          project={props.project}
          start={props.start}
          onClose={props.onClose}
          onOpen={props.onOpen}
          onUpload={props.onUpload}
          onNewFromTemplate={props.onNewFromTemplate}
          onDeleted={() => setDeleted(true)}
        />
      ) : (
        <AuthShell t={t} onClose={props.onClose}>
          {props.confirmToken ? (
            <ConfirmEmail
              key={props.confirmToken}
              cloud={cloud}
              t={t}
              token={props.confirmToken}
              onDone={(ok) => props.onConfirmDone?.(ok)}
            />
          ) : props.resetToken ? (
            <ResetPassword
              key={props.resetToken}
              cloud={cloud}
              t={t}
              locale={props.locale}
              token={props.resetToken}
              onDone={() => props.onResetDone?.()}
            />
          ) : (
            <>
              {deleted && (
                <p className="notice auth-notice" data-testid="cloud-deleted">
                  {t('cloud.deleted')}
                </p>
              )}
              <SignIn cloud={cloud} t={t} locale={props.locale} mail={state.mail} />
            </>
          )}
        </AuthShell>
      )}
    </Dialog>
  );
}

/**
 * The account button at the right of the top bar. Signed out it opens sign-in; signed in it
 * opens the account menu: who is signed in, the workspace, shortcuts into the account dialog.
 */
export function AccountButton(props: {
  cloud: CloudManager;
  state: CloudState;
  t: Translate;
  onOpen(view?: AccountView): void;
}) {
  const { cloud, state, t } = props;
  const me = state.me;
  const live = state.live;
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; right: number } | null>(null);
  const btn = useRef<HTMLButtonElement>(null);
  const list = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!me) setOpen(false);
  }, [me]);
  useEffect(() => {
    if (!open) return;
    const inside = (n: EventTarget | null) =>
      !!n && (btn.current?.contains(n as Node) || list.current?.contains(n as Node));
    const down = (e: MouseEvent) => {
      if (!inside(e.target)) setOpen(false);
    };
    const key = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      setOpen(false);
      btn.current?.focus();
    };
    const away = () => setOpen(false);
    window.addEventListener('mousedown', down);
    window.addEventListener('keydown', key);
    window.addEventListener('resize', away);
    return () => {
      window.removeEventListener('mousedown', down);
      window.removeEventListener('keydown', key);
      window.removeEventListener('resize', away);
    };
  }, [open]);
  useLayoutEffect(() => {
    if (!open || !btn.current) return setPos(null);
    const r = btn.current.getBoundingClientRect();
    setPos({ top: r.bottom + 6, right: Math.max(8, window.innerWidth - r.right) });
  }, [open]);
  useEffect(() => {
    if (open && pos) list.current?.querySelector<HTMLElement>('[role^="menuitem"]')?.focus();
  }, [open, !!pos]);
  const pick = (fn: () => void) => () => {
    setOpen(false);
    fn();
  };
  const name = me ? me.user.name || me.user.email : '';
  const liveTeam = live && me?.teams.find((x) => x.id === live.teamId);
  return (
    <>
      <button
        ref={btn}
        className={`account-btn${me ? ' signed-in' : ''}`}
        onClick={() => (me ? setOpen((o) => !o) : props.onOpen())}
        data-testid="account"
        data-cloud={live ? (live.online ? 'online' : 'offline') : 'none'}
        aria-haspopup={me ? 'menu' : undefined}
        aria-expanded={me ? open : undefined}
        title={me ? `${name} · ${t('cloud.accountMenu')}` : undefined}
      >
        {me ? (
          <>
            <span className="avatar" aria-hidden="true">
              {initialOf(name)}
            </span>
            <span className="account-name">{name}</span>
          </>
        ) : (
          t('cloud.login')
        )}
      </button>
      {open &&
        me &&
        createPortal(
          <div
            ref={list}
            className="acct-menu"
            role="menu"
            aria-label={t('cloud.accountMenu')}
            style={pos ? { top: pos.top, right: pos.right } : { top: 0, right: 0, visibility: 'hidden' }}
            data-testid="account-menu"
          >
            <div className="acct-menu-me">
              <Avatar name={name} size={40} />
              <span>
                <strong>{me.user.name}</strong>
                <span className="acct-mono">{me.user.email}</span>
              </span>
            </div>
            {live && (
              <p className={`acct-menu-live${live.online ? '' : ' off'}`}>
                <span className="acct-dot" />
                {live.online ? t('cloud.liveIn', { name: liveTeam?.name ?? '' }) : t('cloud.liveOffline')}
              </p>
            )}
            <div className="menu-sep" />
            <span className="acct-label">{t('cloud.workspace')}</span>
            {me.teams.map((tm) => (
              <button
                key={tm.id}
                role="menuitemradio"
                aria-checked={tm.id === state.team?.id}
                onClick={pick(() => cloud.selectTeam(tm))}
                data-cloud-team={tm.name}
              >
                <span className="acct-menu-check">{tm.id === state.team?.id && <Glyph name="check" />}</span>
                <span className="grow">{tm.name}</span>
                <span className="acct-role">{roleLabel(t, tm.role)}</span>
              </button>
            ))}
            <button
              role="menuitem"
              className="acct-menu-quiet"
              onClick={pick(() => props.onOpen('newTeam'))}
              data-testid="account-new-team"
            >
              <Glyph name="plus" />
              <span>{t('cloud.newWorkspace')}</span>
            </button>
            <div className="menu-sep" />
            <button
              role="menuitem"
              onClick={pick(() => props.onOpen('projects'))}
              data-testid="account-projects"
            >
              <Glyph name="folder" />
              <span className="grow">{t('cloud.menuProjects')}</span>
            </button>
            {state.team && !state.team.personal && (
              <button
                role="menuitem"
                onClick={pick(() => props.onOpen('members'))}
                data-testid="account-members"
              >
                <Glyph name="team" />
                <span className="grow">{t('cloud.menuMembers')}</span>
              </button>
            )}
            <button
              role="menuitem"
              onClick={pick(() => props.onOpen('templates'))}
              data-testid="account-templates"
            >
              <Glyph name="doc" />
              <span className="grow">{t('cloud.menuTemplates')}</span>
            </button>
            <div className="menu-sep" />
            <button role="menuitem" onClick={pick(() => void cloud.logout())} data-testid="account-logout">
              <Glyph name="logout" />
              <span>{t('cloud.logout')}</span>
            </button>
            <button
              role="menuitem"
              className="danger"
              onClick={pick(() => props.onOpen('delete'))}
              data-testid="account-delete"
            >
              <Glyph name="trash" />
              <span>{t('cloud.deleteAccount')}</span>
            </button>
          </div>,
          document.body,
        )}
    </>
  );
}

/** A template keeps the drawing, not the client data or the history. */
export function stripForTemplate(p: Project): Project {
  return {
    ...p,
    revisions: {},
    meta: { ...p.meta, ownerName: '', ownerAddress: '', installationAddress: '', date: '' },
  };
}

/** Read-only links (live or at a revision), QR codes, handover pack. */
export function ShareDialog(props: {
  cloud: CloudManager;
  t: Translate;
  project: Project;
  projectId: string;
  canShare: boolean;
  onClose(): void;
  onHandover(link: { url: string; revision: string | null }): Promise<void>;
  qrSvg(text: string): Promise<string>;
}) {
  const { cloud, t, project } = props;
  const [shares, setShares] = useState<Share[]>([]);
  const [rev, setRev] = useState<string>('');
  const [qr, setQr] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const revs = useMemo(() => revisionsInOrder(project), [project]);
  const load = () =>
    void cloud
      .client!.shares(props.projectId)
      .then(setShares)
      .catch((e: Error) => setError(e.message));
  useEffect(load, [props.projectId]);
  useEffect(() => {
    for (const s of shares)
      if (!qr[s.token]) void props.qrSvg(s.url).then((svg) => setQr((q) => ({ ...q, [s.token]: svg })));
  }, [shares]);
  const revIndex = (id: string | null) => (id ? (project.revisions[id]?.index ?? '?') : null);
  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog
      title={t('share.title')}
      wide
      onClose={props.onClose}
      actions={<button onClick={props.onClose}>{t('dialog.close')}</button>}
    >
      <p className="small">{t('share.intro')}</p>
      {props.canShare && (
        <div className="chat-input">
          <select value={rev} onChange={(e) => setRev(e.target.value)} data-testid="share-revision">
            <option value="">{t('share.live')}</option>
            {revs.map((r) => (
              <option key={r.id} value={r.id}>
                {t('share.atRevision', { index: r.index })} — {r.description}
              </option>
            ))}
          </select>
          <button
            className="primary"
            disabled={busy}
            onClick={() =>
              void run(async () => (await cloud.client!.createShare(props.projectId, rev || null), load()))
            }
            data-testid="share-create"
          >
            {t('share.create')}
          </button>
        </div>
      )}
      {shares.length === 0 ? (
        <p className="muted">{t('share.none')}</p>
      ) : (
        <ul className="share-list" data-testid="share-list">
          {shares.map((s) => (
            <li key={s.token}>
              <span className="qr" dangerouslySetInnerHTML={{ __html: qr[s.token] ?? '' }} />
              <div>
                <strong>
                  {s.revisionId
                    ? t('share.atRevision', { index: revIndex(s.revisionId) ?? '?' })
                    : t('share.live')}
                </strong>
                <input readOnly value={s.url} onFocus={(e) => e.target.select()} data-testid="share-url" />
                <div className="share-actions">
                  <button onClick={() => void navigator.clipboard?.writeText(s.url)}>
                    {t('share.copy')}
                  </button>
                  <button
                    onClick={() =>
                      void run(() => props.onHandover({ url: s.url, revision: revIndex(s.revisionId) }))
                    }
                    data-testid="share-handover"
                  >
                    {t('share.handover')}
                  </button>
                  {props.canShare && (
                    <button
                      className="link"
                      onClick={() => void run(async () => (await cloud.client!.revokeShare(s.token), load()))}
                    >
                      {t('share.revoke')}
                    </button>
                  )}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
      {error && <p className="warn">{error}</p>}
    </Dialog>
  );
}
